/**
 * What a session last did, as the server read it from the transcript.
 *
 * Two shapes of the same fact. `compact` is a table cell: the operator's last
 * prompt and the agent's latest reply on two lines, the latest tool under them,
 * and the turn's counts in mono. The full shape is a detail panel: every field
 * with its label and its time.
 *
 * An absent summary renders as unmeasured — an em-dash with the reason on it —
 * never as a zero: a session whose transcript cannot be read has not thereby
 * done nothing. And `toolErrors` is shown only when the server counted it: a
 * provider whose record does not mark failures sends none, and "0 hata" would
 * be the invented measurement the rest of the console refuses.
 */

import clsx from 'clsx'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import { Badge } from '@/components/primitives'
import { turnPlainText, turnSegments, type TurnSegment } from '@/lib/turnText'
import type { LastTurn } from './wall'
import './lastTurn.css'

export function LastTurnSummary({
  turn,
  compact,
}: {
  turn?: LastTurn
  /** A table cell rather than a panel. */
  compact?: boolean
}) {
  const t = useT()
  const { locale } = useLocale()

  if (!turn) {
    return (
      <span className="k-lastturn k-lastturn--none" title={t('session.lastTurn.unmeasured')}>
        —
      </span>
    )
  }

  const files = turn.filesTouched?.length ?? 0
  /* Whether the counts are worth a line of the card.
   *
   * `toolCalls` is non-optional, so a zero here is a *measured* zero — the last
   * turn really did use no tools — and it was still printed on nine cards out of
   * eleven as `0 araç çağrısı · 0 hata`. A measured fact an operator cannot act
   * on is what CLAUDE.md calls decoration, and this one cost a third of the
   * card's prose block, which is the part they came for. `19 araç çağrısı ·
   * 0 hata` is a different line: the zero qualifies a number that is there.
   *
   * Compact only. The full form is a labelled detail panel, where "Son tur:
   * 0 araç çağrısı" is a field readout and reading blank would be worse. */
  const hasCounts = turn.toolCalls > 0 || (turn.toolErrors ?? 0) > 0 || files > 0
  const counts = (
    <span className="k-lastturn__counts k-mono">
      {/* "≥" used to trail the whole line as a bare glyph whose meaning lived
          only in a `title` — a fact reachable by neither a touch screen nor a
          keyboard, which the KeyValue primitive already refuses to do. It is a
          word now, and it sits where it applies: in front of the numbers it
          qualifies. */}
      {turn.turnOpen && <span title={t('session.lastTurn.turnOpen')}>{t('session.lastTurn.atLeast')} </span>}
      {t('session.lastTurn.tools', { calls: turn.toolCalls })}
      {turn.toolErrors != null && (
        <>
          {' · '}
          <span className={clsx(turn.toolErrors > 0 && 'k-lastturn__errors')}>
            {t('session.lastTurn.toolErrors', { errors: turn.toolErrors })}
          </span>
        </>
      )}
      {files > 0 && (
        <>
          {' · '}
          {t('session.lastTurn.files', { count: files })}
        </>
      )}
    </span>
  )

  if (compact) {
    // The most recent thing said is what a glance wants. The reply when there
    // is one; otherwise the latest tool; otherwise whatever the last line was.
    const prompt = turnSegments(turn.prompt)
    const latest = turn.reply
      ? { mark: '◂', ...turnSegments(turn.reply) }
      : turn.tool
        ? { mark: '⏺', segments: [{ text: turn.tool, code: true }], harness: false }
        : turn.lastLine
          ? { mark: '', ...turnSegments(turn.lastLine) }
          : null
    // A quiet session whose last line *is* the prompt printed the same string
    // twice, once as what was asked and once as what happened. One of them is
    // not a second fact.
    const flat = (segs: TurnSegment[]) => segs.map((x) => x.text).join('')
    const echo = latest != null && flat(latest.segments) === flat(prompt.segments)
    const showsLatest = latest != null && !echo && latest.segments.length > 0

    /* A harness wrapper is not something anybody said.
     *
     * `<task-notification>` is the harness waking the agent, and on the board it
     * was the *headline* of five cards out of eleven — the most prominent line
     * on the card given to a marker no person wrote, above the reply an operator
     * came to read. `turnSegments` has always known which lines these are; the
     * cell simply rendered them anyway.
     *
     * It still renders when it is all there is. "The harness woke it and nothing
     * has come back yet" is a fact about the session, and an empty cell is not —
     * the same reason `readTurnText` names an empty envelope rather than
     * dropping it. */
    const showsPrompt = prompt.segments.length > 0 && !(prompt.harness && showsLatest)

    return (
      <div className="k-lastturn k-lastturn--compact">
        {showsPrompt && (
          <div className="k-lastturn__line k-lastturn__line--prompt" title={turnPlainText(turn.prompt)}>
            <span className="k-lastturn__mark" aria-hidden>
              ▸
            </span>
            <span className={clsx('k-lastturn__text', prompt.harness && 'k-lastturn__harness k-mono')}>
              <Segments of={prompt.segments} />
            </span>
            {turn.promptAt && (
              <span className="k-lastturn__when k-mono">{formatRelative(turn.promptAt, locale)}</span>
            )}
          </div>
        )}
        {showsLatest && (
          <div className="k-lastturn__line" title={flat(latest.segments)}>
            {latest.mark && (
              <span className="k-lastturn__mark" aria-hidden>
                {latest.mark}
              </span>
            )}
            <span
              className={clsx(
                'k-lastturn__text',
                latest.harness && 'k-lastturn__harness k-mono',
              )}
            >
              <Segments of={latest.segments} />
            </span>
            {turn.reply && turn.replyAt && (
              <span className="k-lastturn__when k-mono">{formatRelative(turn.replyAt, locale)}</span>
            )}
            {latest.mark === '⏺' && turn.toolFailed && (
              <Badge tone="err" subtle>
                {t('session.lastTurn.toolFailed')}
              </Badge>
            )}
          </div>
        )}
        {hasCounts && counts}
      </div>
    )
  }

  return (
    <dl className="k-lastturn k-lastturn--full">
      {turn.prompt && (
        <div className="k-lastturn__row">
          <dt>{t('session.lastTurn.prompt')}</dt>
          <dd>
            <span className="k-lastturn__text">{turn.prompt}</span>
            {turn.promptAt && (
              <span className="k-lastturn__when k-mono">{formatRelative(turn.promptAt, locale)}</span>
            )}
          </dd>
        </div>
      )}
      {turn.reply && (
        <div className="k-lastturn__row">
          <dt>{t('session.lastTurn.reply')}</dt>
          <dd>
            <span className="k-lastturn__text">{turn.reply}</span>
            {turn.replyAt && (
              <span className="k-lastturn__when k-mono">{formatRelative(turn.replyAt, locale)}</span>
            )}
          </dd>
        </div>
      )}
      {turn.tool && (
        <div className="k-lastturn__row">
          <dt>{t('session.lastTurn.tool')}</dt>
          <dd>
            <span className="k-lastturn__text k-mono">{turn.tool}</span>
            {turn.toolFailed && (
              <Badge tone="err" subtle>
                {t('session.lastTurn.toolFailed')}
              </Badge>
            )}
          </dd>
        </div>
      )}
      {!turn.prompt && !turn.reply && !turn.tool && turn.lastLine && (
        <div className="k-lastturn__row">
          <dt>{t('session.lastTurn.lastLine')}</dt>
          <dd>
            <span className="k-lastturn__text k-mono">{turn.lastLine}</span>
          </dd>
        </div>
      )}
      {files > 0 && (
        <div className="k-lastturn__row">
          <dt>{t('session.lastTurn.files', { count: files })}</dt>
          <dd>
            <ul className="k-lastturn__files k-mono">
              {turn.filesTouched!.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </dd>
        </div>
      )}
      <div className="k-lastturn__row">
        <dt>{t('session.lastTurn.title')}</dt>
        <dd>
          {counts}
          {turn.turnOpen && <span className="k-lastturn__note">{t('session.lastTurn.turnOpen')}</span>}
        </dd>
      </div>
    </dl>
  )
}

/** A line's runs, with the fenced ones in the mono face.
 *
 *  The fence characters used to reach the screen — a commit hash arrived as
 *  `` `ce0da40` `` with its backticks, three literal punctuation marks doing the
 *  job this console does with a typeface (CLAUDE.md: machine values are mono,
 *  and it is load-bearing — it is how an operator tells a machine fact from a
 *  human claim without reading it). */
export function Segments({ of }: { of: TurnSegment[] }) {
  return (
    <>
      {of.map((seg, i) =>
        seg.code ? (
          <code key={i} className="k-mono k-lastturn__code">
            {seg.text}
          </code>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  )
}
